import { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import get from "lodash.get"
import keyBy from "lodash.keyby"

import { getConfig } from "@components/Config"
import { useAppContext } from "@components/AppContext"
import {
  indexBankAccountsOperation as indexOperation,
  IndexBankAccountsOutputShape,
  updateBankAccountApproversOperation,
} from "@api/services/transactions"

import useStore from "../helpers/useStore"

const IMMUTABLE_BENEFICIARY_NAME = getConfig("IMMUTABLE_BENEFICIARY_NAME")


const useBankAccountsStore = ({
  projects,
  computeUserFullname,
  getOrganizationControllerId
}) => {
  const { request, identity } = useAppContext()

  const shouldAutoload = false

  const {
    items: bankAccounts,
    addItem: addBankAccount,
    indexItems: indexBankAccounts,
    removeItem: removeBankAccount,
    updateItem: updateBankAccount,
  } = useStore(indexOperation, request, shouldAutoload)

  const bankAccountsMap = useMemo(() =>
    keyBy(bankAccounts, "id")
  , [ bankAccounts ])

  const canApproveBankAccountTransactions = useCallback(bankAccountId => {
    const { id: userId } = identity

    const approverUserIds = get(bankAccountsMap, `${bankAccountId}.approverUserIds`, [])
    const hasNoRestrictions = approverUserIds.length === 0

    if (hasNoRestrictions) {
      return true
    }

    return approverUserIds.includes(userId)
  }, [ identity, bankAccountsMap ])

  const getBankAccount = id => bankAccountsMap[id]

  const isInvalidBankAccount = id => {
    const bankAccount = getBankAccount(id)

    if (!bankAccount) {
      return false
    }

    return bankAccount.isInvalid
  }

  const getBankAccountName = id => {
    const bankAccount = getBankAccount(id)

    if (!bankAccount) {
      return id
    }

    const { beneficiaryName, sourceName } = bankAccount

    return `${beneficiaryName} - ${sourceName}`
  }

  const isInvalidBankAccountTransaction = transaction => {
    const {
      senderSourceId,
      receiverSourceId,
    } = transaction

    const bankAccountsBySourceIdMap = keyBy(bankAccounts, 'sourceId')

    const bankAccount =
      bankAccountsBySourceIdMap[senderSourceId] ||
      bankAccountsBySourceIdMap[receiverSourceId]

    if (!bankAccount) {
      return false
    }

    const { isInvalid } = bankAccount

    return isInvalid
  }

  const isVerifiedBankAccount = bankAccountId => {
    const bankAccount = getBankAccount(bankAccountId)
    const { isSourceVerified } = bankAccount

    return isSourceVerified
  }

  const updateBankAccountApprovers = async (bankAccountId, approverUserIds) => {
    const mutation = { approverUserIds }
    const parameters = { id: bankAccountId, mutation }

    return request(updateBankAccountApproversOperation, parameters)
      .then(({ data }) => updateBankAccount(data))
  }

  const getControllerNameForBankAccount = useCallback((bankAccountId) => {
    const bankAccount = bankAccountsMap[bankAccountId]
    const { beneficiaryName } = bankAccount

    const isRise48BankAccount =
      beneficiaryName.toLowerCase() === IMMUTABLE_BENEFICIARY_NAME.toLowerCase()

    if (isRise48BankAccount) {
      return 'Jerry Herrmann'
    }

    return computeUserFullname(getOrganizationControllerId())
  }, [
    bankAccountsMap,
    computeUserFullname,
    getOrganizationControllerId
  ])

  return {
    bankAccounts,
    addBankAccount,
    bankAccountsMap,
    removeBankAccount,
    indexBankAccounts,
    updateBankAccountApprovers,
    getBankAccountName,
    isInvalidBankAccount,
    isVerifiedBankAccount,
    isInvalidBankAccountTransaction,
    getControllerNameForBankAccount,
    canApproveBankAccountTransactions,
  }
}

const bankAccountsStoreProperties = {
  bankAccounts: PropTypes.arrayOf(IndexBankAccountsOutputShape),
  addBankAccount: PropTypes.func.isRequired,
  bankAccountsMap: PropTypes.shape().isRequired,
  removeBankAccount: PropTypes.func.isRequired,
  indexBankAccounts: PropTypes.func.isRequired,
  updateBankAccountApprovers: PropTypes.func.isRequired,
  getBankAccountName: PropTypes.func.isRequired,
  isInvalidBankAccount: PropTypes.func.isRequired,
  isVerifiedBankAccount: PropTypes.func.isRequired,
  isInvalidBankAccountTransaction: PropTypes.func.isRequired,
  getControllerNameForBankAccount: PropTypes.func.isRequired,
  canApproveBankAccountTransactions: PropTypes.func.isRequired,
}

IndexBankAccountsOutputShape

export default useBankAccountsStore

export {
  indexOperation,
  bankAccountsStoreProperties,
}
